import { Bar, CartesianGrid, ComposedChart, LabelList, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { HORIZONTAL_CATEGORY_AXIS_WIDTH } from "./StackedBarChart";
import { useLegendToggle } from "./useLegendToggle";

export type ComposedBarSeries = { key: string; label: string; color: string };
export type ComposedLineSeries = { key: string; label: string; color: string; strokeWidth?: number };

type ComposedBarLineChartProps = {
    // Um valor null num ponto = célula sem valor: a barra/linha não é desenhada e o rótulo fica vazio
    // (nunca 0 — um 0 leria-se como "custo zero" em vez de "sem dados").
    points: Array<Record<string, string | number | null>>;
    barSeries: Array<ComposedBarSeries>;
    lineSeries: Array<ComposedLineSeries>;
    categoryKey?: string;
    height?: number;
    valueFormatter?: (value: number) => string;
    showBarLabels?: boolean;
    showLineLabels?: boolean;
    // Categorias com rótulos longos (ex.: etapas de funil): força TODOS os ticks e inclina-os. Sem isto o
    // recharts usa interval="preserveEnd" e DEIXA CAIR em silêncio os rótulos que se sobrepõem — o leitor
    // fica com barras sem legenda. Mesmo tratamento do GroupedBarBenchmarkChart.
    angledCategoryLabels?: boolean;
    // Barras horizontais: categorias no eixo vertical e valor à direita de cada barra — para muitas categorias
    // com rótulos longos (ex.: grupos de anúncios). Pensado para barras agrupadas (lineSeries vazio).
    horizontalBars?: boolean;
    // Só com horizontalBars: segunda linha, mais pequena, por baixo do rótulo de cada categoria (ex.: o gasto
    // total do grupo de anúncios). null = só o rótulo.
    categorySubLabel?: (point: Record<string, string | number | null>) => string | null;
};

const HORIZONTAL_VALUE_LABEL_MARGIN = 72;

type HorizontalCategoryTickProps = { x: number; y: number; payload: { value: string }; subLabel: string | null };

const HorizontalCategoryTick = ({ x, y, payload, subLabel }: HorizontalCategoryTickProps) => {
    return (
        <text x={x} y={y} textAnchor="end" fontSize={12} fill="#666">
            <tspan x={x} dy={subLabel ? "-0.2em" : "0.355em"}>
                {payload.value}
            </tspan>
            {subLabel && (
                <tspan x={x} dy="1.3em" fontSize={11} fill="#999">
                    {subLabel}
                </tspan>
            )}
        </text>
    );
};

// Gráfico combinado: barras AGRUPADAS (ex.: Receitas/Despesas por mês) + uma ou mais linhas sobrepostas
// (ex.: resultado acumulado) no MESMO eixo. Uma linha de referência em y=0 ajuda a ler os valores
// negativos (a linha acumulada pode ficar abaixo de zero enquanto as barras ficam acima). Reutilizável.
export const ComposedBarLineChart = ({
    points,
    barSeries,
    lineSeries,
    categoryKey = "monthLabel",
    height = 400,
    valueFormatter,
    showBarLabels = true,
    showLineLabels = true,
    angledCategoryLabels = false,
    horizontalBars = false,
    categorySubLabel,
}: ComposedBarLineChartProps) => {
    const { hiddenKeys, legendProps } = useLegendToggle();
    const formatValue = (value: number) => (valueFormatter ? valueFormatter(value) : String(value));
    const formatLabel = (value: number | null | undefined) => (value === null || value === undefined ? "" : formatValue(Number(value)));
    return (
        <ResponsiveContainer width="100%" height={height}>
            <ComposedChart
                data={points}
                layout={horizontalBars ? "vertical" : "horizontal"}
                margin={{ top: 24, right: horizontalBars ? HORIZONTAL_VALUE_LABEL_MARGIN : 24, bottom: 8, left: 8 }}
                barGap={2}
                barCategoryGap="18%"
            >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" {...(horizontalBars ? { horizontal: false } : { vertical: false })} />
                {horizontalBars ? (
                    <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(value) => formatValue(Number(value))} />
                ) : (
                    <XAxis
                        dataKey={categoryKey}
                        tick={{ fontSize: angledCategoryLabels ? 11 : 12 }}
                        {...(angledCategoryLabels ? { interval: 0 as const, angle: -30, textAnchor: "end", height: 80 } : {})}
                    />
                )}
                {horizontalBars ? (
                    <YAxis
                        type="category"
                        dataKey={categoryKey}
                        tick={
                            categorySubLabel
                                ? (tickProps: Omit<HorizontalCategoryTickProps, "subLabel"> & { index: number }) => (
                                      <HorizontalCategoryTick {...tickProps} subLabel={points[tickProps.index] ? categorySubLabel(points[tickProps.index]) : null} />
                                  )
                                : { fontSize: 12 }
                        }
                        interval={0}
                        width={HORIZONTAL_CATEGORY_AXIS_WIDTH}
                    />
                ) : (
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => formatValue(Number(value))} width={80} />
                )}
                {horizontalBars ? <ReferenceLine x={0} stroke="#b0b0b0" /> : <ReferenceLine y={0} stroke="#b0b0b0" />}
                <Tooltip formatter={(value, name) => [formatValue(Number(value)), name]} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                <Legend {...legendProps} />
                {barSeries.map((serie) => (
                    <Bar key={serie.key} dataKey={serie.key} name={serie.label} fill={serie.color} maxBarSize={54} isAnimationActive={false} hide={hiddenKeys.has(serie.key)}>
                        {showBarLabels && (
                            <LabelList dataKey={serie.key} position={horizontalBars ? "right" : "top"} fontSize={9} fill={serie.color} formatter={formatLabel} />
                        )}
                    </Bar>
                ))}
                {lineSeries.map((serie) => (
                    <Line
                        key={serie.key}
                        type="monotone"
                        dataKey={serie.key}
                        name={serie.label}
                        stroke={serie.color}
                        strokeWidth={serie.strokeWidth ?? 2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 6 }}
                        isAnimationActive={false}
                        hide={hiddenKeys.has(serie.key)}
                    >
                        {showLineLabels && (
                            <LabelList dataKey={serie.key} position="top" fontSize={9} fill={serie.color} formatter={formatLabel} />
                        )}
                    </Line>
                ))}
            </ComposedChart>
        </ResponsiveContainer>
    );
};
